// Pareja "Buy 1, Get 1 Free" (descuento automático BXGY de Shopify).
// Shopify divide la variante en una línea pagada y otra gratis. Cuando el cliente cambia la cantidad de la línea pagada
// (botones +/-, valor escrito o quitar), se actualizan las dos líneas en una sola petición POST /cart/update.js y se
// re-renderizan las secciones igual que hace Horizon tras su propio cambio (assets/component-cart-items.js):
// mismo evento CartLinesUpdateEvent (@shopify/events), mismas secciones y mismo morph; así cajón, página de carrito,
// burbuja del icono y regiones en vivo se actualizan como siempre.
// Se intercepta en fase de captura sobre window (antes de los listeners de Horizon sobre document) y se detiene la
// propagación para que Horizon no lance su propia petición /cart/change.js sobre esas líneas.
// Si la línea pagada no tiene pareja (carrito mixto, cantidades impares) no se hace nada especial.
import { fetchConfig, debounce, resetShimmer, onAnimationEnd, prefersReducedMotion, startViewTransition } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROW_SELECTOR = '.cart-items__table-row[data-kr-key]';
const ROWS_REF_SELECTOR = '.cart-items__table-row[ref="cartItemRows[]"]';
const DEBOUNCE_MS = 300; // igual que #debouncedOnChange en component-cart-items.js

/**
 * Devuelve la línea gratis (misma variante) de una línea pagada, o null si no tiene pareja.
 * @param {Element} component
 * @param {HTMLElement} paidRow
 * @returns {HTMLElement | null}
 */
function findFreeRow(component, paidRow) {
  const variant = paidRow.dataset.krVariant;
  if (!variant) return null;

  for (const row of component.querySelectorAll(ROW_SELECTOR)) {
    if (!(row instanceof HTMLElement) || row === paidRow) continue;
    if (row.dataset.krVariant === variant && row.dataset.krFree === 'true') return row;
  }

  return null;
}

/**
 * Resuelve la pareja (componente, línea pagada, línea gratis) a partir de un elemento interior de la línea pagada.
 * @param {EventTarget | null} target
 */
function resolvePair(target) {
  if (!(target instanceof Element)) return null;

  const component = target.closest('cart-items-component');
  const paidRow = target.closest(ROW_SELECTOR);
  if (!component || !(paidRow instanceof HTMLElement) || paidRow.dataset.krFree !== 'false') return null;

  const freeRow = findFreeRow(component, paidRow);
  if (!freeRow) return null;

  return { component, paidRow, freeRow };
}

/**
 * Cantidad que debe quedar en la línea gratis cuando la pagada pasa a `quantity`: se mueve en la misma diferencia que
 * la pagada (cantidad servida por el servidor = atributo value, que el morph actualiza). En una pareja igualada (N + N)
 * equivale a igualarla a N'; en un carrito mixto, donde Shopify ya ha repartido parte de las unidades gratis a otro
 * producto (p. ej. pagada ×2 + gratis ×1), evita pedir el doble y que la cantidad pagada salte de 2 a 4.
 * @param {{ paidRow: HTMLElement, freeRow: HTMLElement }} pair
 * @param {number} quantity - Nueva cantidad de la línea pagada
 * @returns {number}
 */
function pairedFreeQuantity(pair, quantity) {
  if (quantity <= 0) return 0;

  const paidInput = pair.paidRow.querySelector('cart-quantity-selector-component input');
  const freeInput = pair.freeRow.querySelector('cart-quantity-selector-component input');
  const paidBefore = parseInt(paidInput instanceof HTMLInputElement ? paidInput.defaultValue : '', 10);
  const freeBefore = parseInt(freeInput instanceof HTMLInputElement ? freeInput.defaultValue : '', 10);
  if (!Number.isFinite(paidBefore) || !Number.isFinite(freeBefore)) return quantity;

  return Math.max(0, freeBefore + (quantity - paidBefore));
}

/**
 * Filas del carrito en el orden de cart.items (la fila N corresponde a la línea N de Shopify).
 * @param {Element} component
 * @returns {HTMLElement[]}
 */
function cartRows(component) {
  return Array.from(component.querySelectorAll(ROWS_REF_SELECTOR)).filter((row) => row instanceof HTMLElement);
}

/** @param {Element} component @param {HTMLElement} row */
function lineNumber(component, row) {
  return cartRows(component).indexOf(row) + 1;
}

/** @param {Element} component */
function isDrawer(component) {
  return component.hasAttribute('data-drawer');
}

/** Activa el brillo de carga en los totales de las dos líneas (Horizon lo hace sobre la línea que cambia). */
function shimmerRows(rows) {
  for (const row of rows) {
    const text = row.querySelector('text-component');
    if (text && 'shimmer' in text && typeof text.shimmer === 'function') text.shimmer();
  }
}

/**
 * Réplica de CartItemsComponent.#updateQuantitySelectors: actualiza data-cart-quantity de los selectores de la ficha
 * sumando las líneas de cada variante (el BXGY reparte una variante en varias líneas).
 * @param {{ items?: Array<{ variant_id: number, quantity: number }> }} cart
 */
function updateProductQuantitySelectors(cart) {
  if (!cart.items) return;

  /** @type {Map<string, number>} */
  const quantitiesByVariant = new Map();
  for (const item of cart.items) {
    const variantId = String(item.variant_id);
    quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
  }

  for (const [variantId, quantity] of quantitiesByVariant) {
    for (const selector of document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`)) {
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!input) continue;
      input.setAttribute('data-cart-quantity', String(quantity));
      if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }
}

/** Réplica de CartItemsComponent.#updateCartQuantitySelectorButtonStates. */
function updateCartQuantitySelectorButtonStates() {
  for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
    /** @type {any} */ (selector).updateButtonStates?.();
  }
}

/**
 * Réplica de CartItemsComponent.#handleCartError para la línea pagada.
 * @param {any} component
 * @param {number} line
 * @param {string} errors
 */
function showLineError(component, line, errors) {
  const refs = component.refs ?? {};
  const quantityInput = refs.quantitySelectors?.[line - 1]?.querySelector('input');
  if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;

  const cartItemError = refs[`cartItemError-${line}`];
  const cartItemErrorContainer = refs[`cartItemErrorContainer-${line}`];
  if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
    cartItemError.textContent = errors;
    cartItemErrorContainer.classList.remove('hidden');
  }

  component.dispatchEvent(new CartErrorEvent({ error: errors || 'Cart update failed', code: 'INVALID' }));
}

/**
 * Actualiza la línea pagada y su pareja gratis a la misma cantidad en una sola petición y re-renderiza como Horizon.
 * @param {any} component - <cart-items-component>
 * @param {HTMLElement} paidRow
 * @param {HTMLElement} freeRow
 * @param {number} quantity - Nueva cantidad de la línea pagada (0 = quitar las dos)
 * @param {number} [freeQuantity] - Nueva cantidad de la línea gratis (por defecto, la misma que la pagada)
 */
async function updatePair(component, paidRow, freeRow, quantity, freeQuantity = quantity) {
  const sectionId = component.dataset.sectionId;
  const paidKey = paidRow.dataset.krKey;
  const freeKey = freeRow.dataset.krKey;
  if (!sectionId || !paidKey || !freeKey) return;

  const mode = isDrawer(component) ? 'hydration' : 'full';
  const line = lineNumber(component, paidRow);
  const deferred = CartLinesUpdateEvent.createPromise();
  // Los consumidores (icono, cajón, ficha) encadenan su propio catch; esto solo evita el aviso de rechazo sin gestionar.
  deferred.promise.catch(() => {});

  try {
    component.classList.add('cart-items-disabled');

    const sectionsToUpdate = new Set([sectionId]);
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    }
    for (const id of sectionsToUpdate) sectionRenderer.abortRender(id);

    const body = JSON.stringify({
      updates: { [paidKey]: quantity, [freeKey]: freeQuantity },
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    component.refs?.cartTotal?.shimmer?.();

    component.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [
          { id: paidKey, quantity },
          { id: freeKey, quantity: freeQuantity },
        ],
        promise: deferred.promise,
      })
    );

    const updateUrl = globalThis.Theme?.routes?.cart_update_url || '/cart/update.js';
    const response = await fetch(updateUrl, fetchConfig('json', { body }));
    const parsed = JSON.parse(await response.text());

    resetShimmer(component);

    if (parsed.errors) {
      showLineError(component, line, parsed.errors);
      deferred.reject(new Error(parsed.errors));
      return;
    }

    /** @type {Record<string, string> | null | undefined} */
    const sections = parsed.sections;
    const sectionHTML = sections?.[sectionId];
    const parsedItemCount = Number(parsed.item_count);
    let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

    updateProductQuantitySelectors(parsed);

    const detail = {
      sections: sections ?? undefined,
      items: parsed.items,
      itemCount: newCartItemCount,
      source: 'cart-items-component',
      didError: false,
    };

    if (!sectionHTML) {
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsed), detail });
      await sectionRenderer.renderSection(sectionId, { cache: false, mode });
      updateCartQuantitySelectorButtonStates();
      return;
    }

    const hiddenCount = new DOMParser()
      .parseFromString(sectionHTML, 'text/html')
      .querySelector('[ref="cartItemCount"]')?.textContent;
    newCartItemCount = hiddenCount ? parseInt(hiddenCount, 10) : newCartItemCount;
    detail.itemCount = newCartItemCount;

    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsed), detail });

    sectionRenderer.abortRender(sectionId);
    await morphSection(sectionId, sectionHTML, { mode });
    updateCartQuantitySelectorButtonStates();
  } catch (error) {
    if (error?.name === 'AbortError') return;
    console.error(error);
    deferred.reject(error);
    component.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Failed to update cart', code: 'SERVICE_UNAVAILABLE' })
    );
  } finally {
    component.classList.remove('cart-items-disabled');
  }
}

/**
 * Quita la línea pagada y su pareja: misma animación optimista que CartItemsComponent.onLineItemRemove.
 * @param {{ component: any, paidRow: HTMLElement, freeRow: HTMLElement }} pair
 */
function removePair({ component, paidRow, freeRow }) {
  updatePair(component, paidRow, freeRow, 0);

  const rows = cartRows(component);
  const keys = new Set([paidRow.dataset.key, freeRow.dataset.key]);
  const rowsToRemove = [paidRow, freeRow, ...rows.filter((row) => row.dataset.parentKey && keys.has(row.dataset.parentKey))];
  const isEmptyCart = rowsToRemove.length === rows.length;

  const template = document.getElementById('empty-cart-template');
  if (isEmptyCart && template instanceof HTMLTemplateElement) {
    const clone = document.importNode(template.content, true);
    startViewTransition(
      () => {
        document.getElementById('cart-drawer-heading')?.remove();
        component.replaceChildren(clone);
      },
      [isDrawer(component) ? 'empty-cart-drawer' : 'empty-cart-page']
    );
    return;
  }

  for (const row of rowsToRemove) {
    const remove = () => row.remove();
    if (prefersReducedMotion()) {
      remove();
      continue;
    }
    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add('removing');
    onAnimationEnd(row, remove);
  }
}

const debouncedChange = debounce(
  /**
   * @param {{ component: any, paidRow: HTMLElement, freeRow: HTMLElement }} pair
   * @param {number} quantity
   * @param {number} freeQuantity
   */
  (pair, quantity, freeQuantity) => {
    updatePair(pair.component, pair.paidRow, pair.freeRow, quantity, freeQuantity);
  },
  DEBOUNCE_MS
);

if (!globalThis.__krBogoCartPairs) {
  globalThis.__krBogoCartPairs = true;

  // Cambio de cantidad (+/-, valor escrito): el selector de la línea pagada emite quantity-selector:update (burbujea).
  window.addEventListener(
    ThemeEvents.quantitySelectorUpdate,
    (event) => {
      const pair = resolvePair(event.target);
      if (!pair) return;

      event.stopPropagation();

      const quantity = Number(/** @type {any} */ (event).detail?.quantity);
      if (!Number.isFinite(quantity)) return;

      if (quantity <= 0) {
        debouncedChange.cancel();
        removePair(pair);
        return;
      }

      shimmerRows([pair.paidRow, pair.freeRow]);
      debouncedChange(pair, quantity, pairedFreeQuantity(pair, quantity));
    },
    true
  );

  // Botón de quitar de la línea pagada (on:click="/onLineItemRemove/N" lo enruta Horizon desde document en captura).
  window.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('.cart-items__remove');
      if (!button) return;

      const pair = resolvePair(button);
      if (!pair) return;

      event.stopPropagation();
      event.preventDefault();
      debouncedChange.cancel();
      removePair(pair);
    },
    true
  );
}
